import { Paper, Title, Text, Button, Flex, Stepper, Alert } from "@mantine/core";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { saveToken, removeToken } from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util";
import { successNotification } from "../../shared/utils/successNotification";
import { SignupFlowLayout } from "./SignupFlowLayout";
import {
  GT_SIGNUP_COMPANY_ID_KEY,
  GT_SIGNUP_META_KEY,
  GT_SIGNUP_UPLOAD_TOKEN_KEY,
  clearSignupFlowStorage,
  type SignupRegistrationMeta,
} from "./signupFlowStorage";

export default function SignupDocumentsPage() {
  const navigate = useNavigate();

  const [hydrated, setHydrated] = useState(false);
  const [uploadToken, setUploadToken] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [meta, setMeta] = useState<SignupRegistrationMeta | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setUploadToken(sessionStorage.getItem(GT_SIGNUP_UPLOAD_TOKEN_KEY));
    setCompanyId(sessionStorage.getItem(GT_SIGNUP_COMPANY_ID_KEY));
    const raw = sessionStorage.getItem(GT_SIGNUP_META_KEY);
    if (raw) {
      try {
        setMeta(JSON.parse(raw) as SignupRegistrationMeta);
      } catch {
        setMeta(null);
      }
    }
    setHydrated(true);
  }, []);

  const handleFinish = async () => {
    if (!uploadToken || !companyId) return;

    setLoading(true);
    try {
      saveToken(uploadToken);
      clearSignupFlowStorage();

      successNotification({
        title: "Registration complete",
        message: "You can sign in to build your company profile.",
      });

      const m = meta;
      navigate("/registration-success", {
        state: {
          companyName: m?.companyName ?? "—",
          location: m ? `${m.headquartersCity}, ${m.headquartersCountry}` : "—",
          email: m?.email ?? "—",
        },
      });
    } catch (err) {
      removeToken();
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!hydrated) {
    return (
      <SignupFlowLayout>
        <Paper p="xl" radius="lg" maw={900} mx="auto" style={{ border: "1px solid #E5E7EB" }}>
          <Text ta="center">Loading…</Text>
        </Paper>
      </SignupFlowLayout>
    );
  }

  if (!uploadToken || !companyId) {
    return (
      <SignupFlowLayout>
        <Paper p="xl" radius="lg" maw={900} mx="auto" style={{ border: "1px solid #E5E7EB" }}>
          <Alert color="yellow" title="Session expired or missing">
            Complete company details first, or log in if you already have an account.
          </Alert>
          <Flex justify="center" gap="md" mt="lg">
            <Button component={Link} to="/signup">
              Start signup
            </Button>
            <Button component={Link} to="/login" variant="default">
              Log in
            </Button>
          </Flex>
        </Paper>
      </SignupFlowLayout>
    );
  }

  return (
    <SignupFlowLayout>
      <Paper p="xl" radius="lg" maw={900} mx="auto" style={{ border: "1px solid #E5E7EB" }}>
        <Stepper active={2} mb="xl">
          <Stepper.Step label="Account" description="Verify email" />
          <Stepper.Step label="Company details" />
          <Stepper.Step label="Finish" />
        </Stepper>

        <Title order={4} mb={4} fw={400}>
          Almost done
        </Title>
        <Text fz="sm" c="slate.6" mb="lg">
          Verification documents are no longer required at signup. Continue to complete registration — you will confirm
          authority when you create your full company profile.
        </Text>

        <Flex justify="flex-end" mt="xl">
          <Button size="md" px="xl" onClick={handleFinish} loading={loading}>
            Complete registration
          </Button>
        </Flex>
      </Paper>
    </SignupFlowLayout>
  );
}
